import { safeStorage, type SafeStorage } from '@/shared/lib/safe-storage'

export const REFRESH_TOKEN_KEY = 'app:refresh-token'

/**
 * Holds the token pair of the signed-in user (ADR-0009). The access token lives in memory only; the
 * refresh token is kept in localStorage so the session survives reloads and is shared by tabs. When
 * storage is unavailable it stays in memory and the session ends with the page.
 */
export interface TokenStore {
  getAccessToken(): string | null
  /** Always read from storage: another tab may have replaced it. */
  getRefreshToken(): string | null
  set(tokens: { access_token: string; refresh_token: string }): void
  clear(): void
}

export function createTokenStore(storage: SafeStorage = safeStorage('local')): TokenStore {
  let accessToken: string | null = null
  // Only used while storage refuses to keep the refresh token.
  let unsavedRefreshToken: string | null = null

  return {
    getAccessToken: () => accessToken,
    getRefreshToken: () => unsavedRefreshToken ?? storage.get(REFRESH_TOKEN_KEY),
    set(tokens) {
      accessToken = tokens.access_token
      storage.set(REFRESH_TOKEN_KEY, tokens.refresh_token)
      const saved = storage.get(REFRESH_TOKEN_KEY) === tokens.refresh_token
      unsavedRefreshToken = saved ? null : tokens.refresh_token
    },
    clear() {
      accessToken = null
      unsavedRefreshToken = null
      storage.remove(REFRESH_TOKEN_KEY)
    },
  }
}
